import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getProductByBarcode } from '../services/productService';
import {
  addStockReturn,
  getStockReturns,
  getStockOutRecordForBarcode
} from '../services/stockService';
import { Product, StockOut, StockReturnRecord } from '../types';

const BTN_COLOR = '#78121c';
const BTN_HOVER = '#5c0e15';

type ReturnReason = StockReturnRecord['reason'];

const RETURN_REASONS: { value: ReturnReason; label: string }[] = [
  { value: 'damaged', label: 'Damaged' },
  { value: 'customer_changed_mind', label: 'Customer Changed Mind' },
  { value: 'wrong_item_delivered', label: 'Wrong Item Delivered' },
  { value: 'warranty_return', label: 'Warranty Return' },
  { value: 'other', label: 'Other' }
];

const REASON_LABEL_MAP: Record<ReturnReason, string> = {
  damaged: 'Damaged',
  customer_changed_mind: 'Customer Changed Mind',
  wrong_item_delivered: 'Wrong Item Delivered',
  warranty_return: 'Warranty Return',
  other: 'Other'
};

const formatDate = (dateField: any): string => {
  if (!dateField) return '–';
  if (dateField.toDate && typeof dateField.toDate === 'function') {
    return dateField.toDate().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  }
  if (dateField instanceof Date) {
    return dateField.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  }
  return '–';
};

interface ScannedReturnItem {
  barcode: string;
  product: Product;
  stockOutRecord: StockOut & { docId: string };
}

export const StockReturn: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);

  const [barcodeInput, setBarcodeInput] = useState('');
  const [scannedItems, setScannedItems] = useState<ScannedReturnItem[]>([]);
  const [reason, setReason] = useState<ReturnReason>('damaged');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [scanLoading, setScanLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [returnHistory, setReturnHistory] = useState<StockReturnRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const records = await getStockReturns(50);
      setReturnHistory(records);
    } catch {
      /* silent */
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleScanBarcode = async () => {
    const raw = barcodeInput.trim();
    if (!raw) return;

    const barcodes = raw.split(/[\n,\s]+/).map(b => b.trim()).filter(Boolean);
    const unique = Array.from(new Set(barcodes));

    const existingSet = new Set(scannedItems.map(i => i.barcode));
    const duplicates = unique.filter(b => existingSet.has(b));
    const toLookup = unique.filter(b => !existingSet.has(b));

    if (toLookup.length === 0) {
      setError(`All barcodes are already in the return list.`);
      setBarcodeInput('');
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }

    setScanLoading(true);
    setError('');

    try {
      const results = await Promise.all(
        toLookup.map(async (barcode) => {
          const [product, outRecord] = await Promise.all([
            getProductByBarcode(barcode),
            getStockOutRecordForBarcode(barcode)
          ]);
          return { barcode, product, outRecord };
        })
      );

      const validItems: ScannedReturnItem[] = [];
      const errors: string[] = [];

      for (const { barcode, product, outRecord } of results) {
        if (!product) {
          errors.push(`"${barcode}" — product not found`);
        } else if (!outRecord) {
          errors.push(`"${barcode}" — not stocked out`);
        } else {
          validItems.push({ barcode, product, stockOutRecord: outRecord });
        }
      }

      if (duplicates.length > 0) {
        errors.push(`${duplicates.length} duplicate(s) skipped`);
      }

      if (validItems.length > 0) {
        setScannedItems(prev => [...prev, ...validItems]);
      }

      setBarcodeInput('');
      if (errors.length > 0) {
        setError(`Added ${validItems.length} item(s). Issues: ${errors.join('; ')}`);
      }
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (err: unknown) {
      setError((err as Error).message || 'Error looking up barcodes');
    } finally {
      setScanLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleScanBarcode();
    }
  };

  const handleRemoveItem = (index: number) => {
    setScannedItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitReturn = async () => {
    if (!user || scannedItems.length === 0) return;

    if (reason === 'other' && !notes.trim()) {
      setError('Please enter notes describing the return reason when "Other" is selected.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    let processed = 0;
    try {
      for (let i = 0; i < scannedItems.length; i++) {
        const item = scannedItems[i];
        try {
          await addStockReturn(
            item.barcode,
            item.product.name,
            1,
            user.uid,
            user.displayName ?? 'User',
            reason,
            item.stockOutRecord.docId,
            notes
          );
          processed++;
        } catch (err: unknown) {
          const msg = (err as Error).message || 'Unknown error';
          if (processed > 0) {
            setError(`Processed ${processed} of ${scannedItems.length} items. Error on "${item.product.name}" (${item.barcode}): ${msg}`);
          } else {
            setError(`Error on "${item.product.name}" (${item.barcode}): ${msg}`);
          }
          setScannedItems(prev => prev.slice(i));
          await loadHistory();
          return;
        }
      }

      setSuccess(`Successfully returned ${processed} item${processed > 1 ? 's' : ''} back to stock.`);
      setScannedItems([]);
      setReason('damaged');
      setNotes('');
      await loadHistory();
      setTimeout(() => inputRef.current?.focus(), 100);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Return Items</Typography>
        <Button onClick={() => navigate('/dashboard')} size="small" sx={{ color: BTN_COLOR }}>
          ← Dashboard
        </Button>
      </Box>

      {/* Step 1: Return reason */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Step 1: Select the return reason
        </Typography>
        <TextField
          select
          fullWidth
          size="small"
          label="Return Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value as ReturnReason)}
          sx={{ mb: 2, maxWidth: 360 }}
        >
          {RETURN_REASONS.map((r) => (
            <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
          ))}
        </TextField>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Notes {reason === 'other' ? '(required)' : '(optional)'}
        </Typography>
        <TextField
          fullWidth
          size="small"
          multiline
          rows={2}
          label="Notes"
          placeholder={reason === 'other' ? 'Describe the return reason...' : 'Any additional notes...'}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          required={reason === 'other'}
          sx={{ maxWidth: 500 }}
        />
      </Paper>

      {/* Step 2: Scan barcodes */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Step 2: Scan or paste barcodes of returned items
        </Typography>
        <TextField
          inputRef={inputRef}
          fullWidth
          size="medium"
          multiline
          minRows={1}
          maxRows={4}
          placeholder="Scan a barcode, or paste multiple barcodes (one per line / comma-separated)"
          value={barcodeInput}
          onChange={(e) => setBarcodeInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={scanLoading}
          autoFocus
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleScanBarcode}
                  disabled={scanLoading || !barcodeInput.trim()}
                  sx={{ bgcolor: BTN_COLOR, '&:hover': { bgcolor: BTN_HOVER }, minWidth: 60 }}
                >
                  {scanLoading ? 'Adding...' : 'Add'}
                </Button>
              </InputAdornment>
            ),
            sx: {
              alignItems: 'flex-start',
              '& fieldset': { borderColor: BTN_COLOR },
              '&:hover fieldset': { borderColor: BTN_COLOR, borderWidth: '2px' },
              '&.Mui-focused fieldset': { borderColor: BTN_COLOR, borderWidth: '2px' }
            }
          }}
          sx={{ maxWidth: 500 }}
        />
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          Tip: Paste multiple barcodes at once for bulk returns. Press Enter or click Add.
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          {scannedItems.length} item{scannedItems.length !== 1 ? 's' : ''} scanned for return
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Scanned items table */}
      {scannedItems.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Items to Return ({scannedItems.length})
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setScannedItems([])}
              sx={{ color: BTN_COLOR, borderColor: BTN_COLOR, '&:hover': { borderColor: BTN_HOVER } }}
            >
              Clear All
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: 350 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Barcode</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Sold Date</TableCell>
                  <TableCell>Stock-Out Reason</TableCell>
                  <TableCell>Stock-Out Notes</TableCell>
                  <TableCell align="center">Remove</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scannedItems.map((item, idx) => (
                  <TableRow key={item.barcode}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{item.barcode}</TableCell>
                    <TableCell>{item.product.name}</TableCell>
                    <TableCell>{item.product.categoryCode || item.product.category}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.stockOutRecord.date)}</TableCell>
                    <TableCell>{item.stockOutRecord.reason}</TableCell>
                    <TableCell>{item.stockOutRecord.notes || '–'}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleRemoveItem(idx)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ p: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Confirming will return {scannedItems.length} item{scannedItems.length !== 1 ? 's' : ''} back to stock.
              Each barcode will be available for stock-out again.
            </Alert>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmitReturn}
              disabled={submitting}
              sx={{ bgcolor: BTN_COLOR, '&:hover': { bgcolor: BTN_HOVER }, fontWeight: 600 }}
            >
              {submitting ? 'Processing...' : `Confirm Return (${scannedItems.length} item${scannedItems.length !== 1 ? 's' : ''})`}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Return history */}
      <Paper>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Return History</Typography>
          <Button size="small" onClick={loadHistory} sx={{ color: BTN_COLOR }}>
            Refresh
          </Button>
        </Box>
        <TableContainer sx={{ maxHeight: 420 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Barcode</TableCell>
                <TableCell>Product Name</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Processed By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">Loading return history...</TableCell>
                </TableRow>
              ) : returnHistory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">No return records yet.</TableCell>
                </TableRow>
              ) : (
                returnHistory.map((record, idx) => (
                  <TableRow key={record.id || idx}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(record.date)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{record.barcode}</TableCell>
                    <TableCell>{record.productName}</TableCell>
                    <TableCell>{record.quantity}</TableCell>
                    <TableCell>
                      <Chip label={REASON_LABEL_MAP[record.reason] || record.reason} size="small" />
                    </TableCell>
                    <TableCell>{record.notes || '–'}</TableCell>
                    <TableCell>{record.userName}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};
